import { type FC, useState } from "react";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
	type AspectRatio,
	CAROUSEL_MAX_BODY_LENGTH,
	CAROUSEL_MAX_HEADLINE_LENGTH,
	CAROUSEL_MAX_SLIDES,
	CAROUSEL_MAX_TOPIC_LENGTH,
	CAROUSEL_MIN_SLIDES,
	type CarouselBackgroundStyle,
	type CarouselSlide,
	type LogoPosition,
} from "shared";
import { useEventCallback } from "usehooks-ts";
import { useForm } from "../../common/form/useForm";
import { Modal, useModal } from "../../common/modal/Modal";
import { ExtraForm } from "../../common/pure/ExtraFormGroup";
import { useCompany } from "../../company/useCompany";
import { Button } from "../../components/Button";
import { ContentLoading } from "../../components/ContentLoading";
import { LinkGen } from "../../LinkGen";
import { useMediaActions } from "../useMediaActions";
import "./ModalGenerateCarousel.css";

type ViewState = "form" | "generating" | "editor";

interface FormValues {
	topic: string;
	slideCount: number;
	aspectRatio: AspectRatio;
	logoPosition: LogoPosition;
	includeBrandColors: boolean;
	backgroundStyle: CarouselBackgroundStyle;
}

const ASPECT_OPTIONS: Array<{ value: AspectRatio; label: string }> = [
	{ value: "1:1", label: "Square (1:1)" },
	{ value: "4:5", label: "Portrait (4:5)" },
	{ value: "9:16", label: "Story (9:16)" },
];

const LOGO_POSITION_OPTIONS: Array<{ value: LogoPosition; label: string }> = [
	{ value: "top-left", label: "Top left" },
	{ value: "top-right", label: "Top right" },
	{ value: "bottom-left", label: "Bottom left" },
	{ value: "bottom-right", label: "Bottom right" },
	{ value: "none", label: "No logo" },
];

const BACKGROUND_STYLE_OPTIONS: Array<{
	value: CarouselBackgroundStyle;
	label: string;
}> = [
	{ value: "photo", label: "Photo" },
	{ value: "gradient", label: "Gradient" },
	{ value: "solid", label: "Solid" },
];

function aspectRatioToPaddingTop(ratio: AspectRatio): string {
	switch (ratio) {
		case "4:5":
			return "125%";
		case "9:16":
			return "177.78%";
		default:
			return "100%";
	}
}

export function ModalGenerateCarousel() {
	const { close } = useModal();
	const navigate = useNavigate();
	const company = useCompany();
	const {
		generateCarousel,
		regenerateCarouselSlide,
		exportCarousel,
		generateCarouselCaption,
		invalidateMedia,
	} = useMediaActions();

	const [viewState, setViewState] = useState<ViewState>("form");
	const [topic, setTopic] = useState("");
	const [slides, setSlides] = useState<CarouselSlide[]>([]);
	const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(
		null,
	);

	const hasLogo = !!company.logo_url;
	const hasBrandColors =
		company.brand_colors && company.brand_colors.length > 0;

	const form = useForm({
		initialValues: {
			topic: "",
			slideCount: 3,
			aspectRatio: "1:1",
			logoPosition: hasLogo ? "bottom-right" : "none",
			includeBrandColors: hasBrandColors ?? false,
			backgroundStyle: "photo",
		} satisfies FormValues,
		onSubmit: async (values) => {
			setViewState("generating");
			try {
				const result = await generateCarousel.mutateAsync({
					topic: values.topic,
					slideCount: values.slideCount,
					aspectRatio: values.aspectRatio,
					logoPosition: values.logoPosition,
					includeBrandColors: values.includeBrandColors,
					backgroundStyle: values.backgroundStyle,
				});
				setTopic(result.topic);
				setSlides(result.slides);
				setViewState("editor");
			} catch {
				setViewState("form");
			}
		},
	});

	const handleSlideTextChange = useEventCallback(
		(index: number, field: "headline" | "body", value: string) => {
			setSlides((prev) =>
				prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
			);
		},
	);

	const handleToggleLogo = useEventCallback((index: number) => {
		setSlides((prev) =>
			prev.map((s, i) =>
				i === index ? { ...s, logoVisible: !s.logoVisible } : s,
			),
		);
	});

	const handleBackgroundStyleChange = useEventCallback(
		(index: number, style: CarouselBackgroundStyle) => {
			setSlides((prev) =>
				prev.map((s, i) =>
					i === index ? { ...s, backgroundStyle: style } : s,
				),
			);
		},
	);

	const handleRegenerateSlide = useEventCallback(async (index: number) => {
		const slide = slides[index];
		if (!slide) return;
		setRegeneratingIndex(index);
		try {
			const result = await regenerateCarouselSlide.mutateAsync({
				topic,
				slideIndex: index,
				totalSlides: slides.length,
				headline: slide.headline,
				body: slide.body,
				aspectRatio: form.values.aspectRatio,
				backgroundStyle: slide.backgroundStyle,
				includeBrandColors: form.values.includeBrandColors,
			});
			setSlides((prev) =>
				prev.map((s, i) =>
					i === index
						? {
								...result.slide,
								headline: s.headline,
								body: s.body,
								logoVisible: s.logoVisible,
							}
						: s,
				),
			);
		} finally {
			setRegeneratingIndex(null);
		}
	});

	const handleDownload = useEventCallback(async () => {
		const result = await exportCarousel.mutateAsync({
			slides,
			aspectRatio: form.values.aspectRatio,
			logoPosition: form.values.logoPosition,
		});
		await invalidateMedia();
		// Trigger browser download of the ZIP
		const link = document.createElement("a");
		link.href = result.zipUrl;
		link.download = "carousel.zip";
		document.body.appendChild(link);
		link.click();
		link.remove();
	});

	const handleCreatePost = useEventCallback(async () => {
		const result = await exportCarousel.mutateAsync({
			slides,
			aspectRatio: form.values.aspectRatio,
			logoPosition: form.values.logoPosition,
		});
		await invalidateMedia();

		// Generate the post copy from the carousel topic + slide text.
		// Non-blocking: if it fails, still create the post (just without copy).
		let generatedContent = "";
		try {
			const captionResult = await generateCarouselCaption.mutateAsync({
				topic,
				slides,
			});
			generatedContent = captionResult.caption;
		} catch {
			// Swallow — the post is still created without pre-filled copy.
		}

		close();
		navigate(`${LinkGen.PostCreate()}?mediaIds=${result.mediaIds.join(",")}`, {
			state: { generatedContent },
		});
	});

	const handleBackToForm = useEventCallback(() => {
		setViewState("form");
		setSlides([]);
		setTopic("");
	});

	return (
		<Modal size="xl">
			{viewState === "form" && (
				<Form onSubmit={form.handleSubmit}>
					<h4 className="mb-3">Generate Carousel</h4>

					{!hasBrandColors && !hasLogo && (
						<div className="alert alert-info py-2 mb-3">
							<small>
								Tip: add your logo and brand colors in company settings for
								consistently branded slides.
							</small>
						</div>
					)}

					<ExtraForm.Group label="Topic">
						<ExtraForm.Control
							as="textarea"
							rows={3}
							maxLength={CAROUSEL_MAX_TOPIC_LENGTH}
							placeholder="Describe what this carousel should be about..."
							{...form.getFieldProps("topic")}
							{...form.getFieldState("topic")}
						/>
						<small className="text-muted">
							{form.values.topic.length}/{CAROUSEL_MAX_TOPIC_LENGTH}
						</small>
					</ExtraForm.Group>

					<div className="mb-3">
						<span className="form-label d-block">Number of slides</span>
						<div className="d-flex gap-2">
							{Array.from(
								{ length: CAROUSEL_MAX_SLIDES - CAROUSEL_MIN_SLIDES + 1 },
								(_, i) => CAROUSEL_MIN_SLIDES + i,
							).map((count) => (
								<Form.Check
									key={count}
									type="radio"
									id={`slideCount-${count}`}
									name="slideCount"
									label={String(count)}
									checked={form.values.slideCount === count}
									onChange={() => form.setFieldValue("slideCount", count)}
								/>
							))}
						</div>
					</div>

					<div className="mb-3">
						<span className="form-label d-block">Aspect ratio</span>
						<div className="d-flex gap-2">
							{ASPECT_OPTIONS.map((option) => (
								<Form.Check
									key={option.value}
									type="radio"
									id={`aspect-${option.value}`}
									name="aspectRatio"
									label={option.label}
									checked={form.values.aspectRatio === option.value}
									onChange={() =>
										form.setFieldValue("aspectRatio", option.value)
									}
								/>
							))}
						</div>
					</div>

					<div className="mb-3">
						<span className="form-label d-block">Background style</span>
						<div className="d-flex gap-2">
							{BACKGROUND_STYLE_OPTIONS.map((option) => (
								<Form.Check
									key={option.value}
									type="radio"
									id={`bgStyle-${option.value}`}
									name="backgroundStyle"
									label={option.label}
									checked={form.values.backgroundStyle === option.value}
									onChange={() =>
										form.setFieldValue("backgroundStyle", option.value)
									}
								/>
							))}
						</div>
						<small className="text-muted d-block mt-1">
							{form.values.backgroundStyle === "photo"
								? "AI generates a background image for each slide."
								: "Slides use your brand colors as a solid or gradient fill."}
						</small>
					</div>

					<div className="mb-3">
						<span className="form-label d-block">Logo position</span>
						<div className="d-flex gap-2 flex-wrap">
							{LOGO_POSITION_OPTIONS.map((option) => (
								<Form.Check
									key={option.value}
									type="radio"
									id={`logoPos-${option.value}`}
									name="logoPosition"
									label={option.label}
									checked={form.values.logoPosition === option.value}
									onChange={() =>
										form.setFieldValue("logoPosition", option.value)
									}
									disabled={option.value !== "none" && !hasLogo}
								/>
							))}
						</div>
						{!hasLogo && (
							<small className="text-muted d-block mt-1">
								No logo uploaded. Add one in company settings.
							</small>
						)}
					</div>

					<div className="mb-3">
						<Form.Check
							type="checkbox"
							id="includeBrandColors"
							label="Use brand colors"
							checked={form.values.includeBrandColors}
							onChange={(e) =>
								form.setFieldValue("includeBrandColors", e.target.checked)
							}
							disabled={!hasBrandColors}
						/>
						{hasBrandColors && (
							<div className="d-flex gap-1 mt-1">
								{company.brand_colors?.map((color) => (
									<div
										key={color}
										className="carousel-color-swatch"
										style={{ backgroundColor: color }}
										title={color}
									/>
								))}
							</div>
						)}
					</div>

					<Button
						loading={generateCarousel.isPending}
						icon="bolt"
						className="mt-3"
						full
						type="submit"
						disabled={!form.values.topic.trim()}
					>
						Generate Carousel
					</Button>
				</Form>
			)}

			{viewState === "generating" && (
				<div className="text-center py-5">
					<ContentLoading />
					<p className="mt-3">Generating your carousel...</p>
					<small className="text-muted">
						This can take up to 30 seconds for a 5-slide carousel.
					</small>
				</div>
			)}

			{viewState === "editor" && (
				<div>
					<div className="d-flex justify-content-between align-items-center mb-3">
						<h4 className="m-0">Edit Carousel</h4>
						<Button
							variant="ghost"
							icon="arrow_back_ios"
							onClick={handleBackToForm}
						>
							Start over
						</Button>
					</div>

					<div className="carousel-editor-scroll">
						{slides.map((slide) => (
							<CarouselSlideCard
								key={slide.index}
								slide={slide}
								aspectRatio={form.values.aspectRatio}
								logoPosition={form.values.logoPosition}
								logoUrl={company.logo_url ?? null}
								isRegenerating={regeneratingIndex === slide.index}
								onTextChange={handleSlideTextChange}
								onToggleLogo={handleToggleLogo}
								onBackgroundStyleChange={handleBackgroundStyleChange}
								onRegenerate={handleRegenerateSlide}
							/>
						))}
					</div>

					<div className="d-flex flex-column gap-2 mt-4">
						<Button
							icon="download"
							full
							onClick={handleDownload}
							loading={exportCarousel.isPending}
						>
							Download all (ZIP)
						</Button>
						<Button
							variant="primary"
							icon="edit"
							full
							onClick={handleCreatePost}
							loading={
								exportCarousel.isPending || generateCarouselCaption.isPending
							}
						>
							Save and create post
						</Button>
					</div>
				</div>
			)}
		</Modal>
	);
}

interface CarouselSlideCardProps {
	slide: CarouselSlide;
	aspectRatio: AspectRatio;
	logoPosition: LogoPosition;
	logoUrl: string | null;
	isRegenerating: boolean;
	onTextChange: (
		index: number,
		field: "headline" | "body",
		value: string,
	) => void;
	onToggleLogo: (index: number) => void;
	onBackgroundStyleChange: (
		index: number,
		style: CarouselBackgroundStyle,
	) => void;
	onRegenerate: (index: number) => void;
}

const CarouselSlideCard: FC<CarouselSlideCardProps> = ({
	slide,
	aspectRatio,
	logoPosition,
	logoUrl,
	isRegenerating,
	onTextChange,
	onToggleLogo,
	onBackgroundStyleChange,
	onRegenerate,
}) => {
	const showLogo = slide.logoVisible && logoPosition !== "none" && !!logoUrl;

	return (
		<div className="carousel-slide-card">
			<div className="carousel-slide-header">
				<strong>Slide {slide.index + 1}</strong>
				<Button
					variant="ghost"
					icon="refresh"
					onClick={() => onRegenerate(slide.index)}
					loading={isRegenerating}
				>
					Regenerate
				</Button>
			</div>

			<div
				className="carousel-slide-preview"
				style={{ paddingTop: aspectRatioToPaddingTop(aspectRatio) }}
			>
				<img
					src={slide.backgroundImageUrl}
					alt={`slide ${slide.index + 1} background`}
					className="carousel-slide-bg"
				/>
				<div className="carousel-slide-scrim" />
				{showLogo && (
					<img
						src={logoUrl ?? ""}
						alt="logo"
						className={`carousel-slide-logo carousel-slide-logo-${logoPosition}`}
					/>
				)}
				<div className="carousel-slide-text">
					<textarea
						className="carousel-slide-headline-input"
						value={slide.headline}
						maxLength={CAROUSEL_MAX_HEADLINE_LENGTH}
						placeholder="Headline"
						rows={2}
						onChange={(e) =>
							onTextChange(slide.index, "headline", e.target.value)
						}
					/>
					<textarea
						className="carousel-slide-body-input"
						value={slide.body}
						maxLength={CAROUSEL_MAX_BODY_LENGTH}
						placeholder="Body copy"
						rows={2}
						onChange={(e) => onTextChange(slide.index, "body", e.target.value)}
					/>
				</div>
			</div>

			<div className="carousel-slide-controls">
				<div className="d-flex gap-2 align-items-center">
					<span className="form-label m-0 me-1">Background:</span>
					{BACKGROUND_STYLE_OPTIONS.map((opt) => (
						<Form.Check
							key={opt.value}
							inline
							type="radio"
							id={`bg-${slide.index}-${opt.value}`}
							name={`bg-${slide.index}`}
							label={opt.label}
							checked={slide.backgroundStyle === opt.value}
							onChange={() => onBackgroundStyleChange(slide.index, opt.value)}
						/>
					))}
				</div>
				<Form.Check
					type="switch"
					id={`logo-${slide.index}`}
					label="Show logo"
					checked={slide.logoVisible}
					onChange={() => onToggleLogo(slide.index)}
					disabled={logoPosition === "none" || !logoUrl}
				/>
			</div>
		</div>
	);
};
